import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocument, usedFontSizes } from "@/office/word/document";

export const lektionSchriftgroesse: Lesson = {
  id: "schriftgroesse",
  title: "Schriftgröße und Textgrößen-Hierarchie",
  summary: "Punktgrößen verstehen und Überschriften vom Fließtext abheben.",
  durationMinutes: 7,
  learningGoals: [
    "Die Maßeinheit Punkt (pt) einordnen",
    "Schriftgrößen gezielt zuweisen",
    "Mit Größenunterschieden eine klare Gliederung erzeugen",
  ],
  steps: [
    {
      id: "punkt-verstehen",
      title: "Was bedeutet \"pt\"?",
      explanation: [
        "Die Schriftgröße wird in Punkt (pt) angegeben. Ein Punkt entspricht etwa " +
          "0,35 Millimetern. Word verwendet standardmäßig 11 pt – eine Größe, die auf " +
          "Papier und am Bildschirm gut lesbar ist.",
        "Für Fließtext haben sich 10 bis 12 pt bewährt. Alles darunter wird beim " +
          "Ausdrucken schnell anstrengend, alles darüber wirkt im Fließtext unruhig. " +
          "Überschriften liegen typischerweise zwischen 14 und 20 pt.",
      ],
      keyPoints: [
        "Fließtext: 10–12 pt. Überschriften: 14–20 pt.",
        "Strg + Umschalt + > vergrößert, Strg + Umschalt + < verkleinert die Auswahl.",
        "Die beiden Buttons \"A▲\" und \"A▼\" im Menüband machen dasselbe.",
      ],
      task: {
        instruction: "Setze eine Zeile deines Dokuments auf genau 16 pt.",
        hint: "Zeile markieren, dann im Feld neben der Schriftart die 16 eintippen und mit Enter bestätigen.",
        check: async () => {
          const snapshot = await readDocument();
          if (snapshot.runs.length === 0) {
            return fail("Im Dokument steht noch kein Text.", "Schreibe zuerst etwas Text.");
          }

          const found = snapshot.filledParagraphs.find((paragraph) =>
            paragraph.runs.every((run) => run.size === 16),
          );
          if (!found) {
            const sizes = usedFontSizes(snapshot);
            return fail(
              `Noch keine Zeile mit genau 16 pt gefunden. Aktuell verwendet: ${
                sizes.map((size) => `${size} pt`).join(", ") || "keine erkennbare Größe"
              }.`,
              "Achte darauf, die komplette Zeile zu markieren – auch das letzte Wort.",
            );
          }
          return pass("Passt – die Zeile steht jetzt auf 16 pt.");
        },
      },
    },
    {
      id: "hierarchie-aufbauen",
      title: "Eine Größen-Hierarchie aufbauen",
      explanation: [
        "Größenunterschiede sind das wichtigste Werkzeug, um einem Text Struktur zu " +
          "geben. Leserinnen und Leser erkennen an der Größe sofort, was Überschrift " +
          "und was Fließtext ist – noch bevor sie ein einziges Wort gelesen haben.",
        "Damit der Unterschied wirkt, braucht es einen deutlichen Sprung. 12 pt neben " +
          "13 pt sieht nach Versehen aus, 12 pt neben 18 pt nach Absicht. Eine gute " +
          "Faustregel ist ein Unterschied von mindestens vier Punkt.",
      ],
      keyPoints: [
        "Zu kleine Unterschiede wirken wie ein Fehler, nicht wie Gestaltung.",
        "Halte dich pro Dokument an wenige feste Größen statt an viele Zwischenschritte.",
      ],
      task: {
        instruction:
          "Baue eine kleine Hierarchie: eine Überschriftszeile mit mindestens 16 pt und " +
          "darunter einen Fließtextabsatz mit 11 oder 12 pt.",
        hint: "Die Überschrift kann die Zeile aus dem vorherigen Schritt sein. Schreibe darunter zwei bis drei Sätze.",
        check: async () => {
          const snapshot = await readDocument();

          const heading = snapshot.filledParagraphs.find((paragraph) =>
            paragraph.runs.every((run) => typeof run.size === "number" && run.size >= 16),
          );
          const body = snapshot.filledParagraphs.find(
            (paragraph) =>
              paragraph.index !== heading?.index &&
              paragraph.runs.every((run) => run.size === 11 || run.size === 12),
          );
          const headingAbove = Boolean(heading && body && heading.index < body.index);

          return fromCriteria(
            [
              criterion("Es gibt eine Zeile mit mindestens 16 pt", Boolean(heading)),
              criterion("Es gibt einen Fließtextabsatz mit 11 oder 12 pt", Boolean(body)),
              criterion("Die größere Zeile steht oberhalb des Fließtextes", headingAbove),
            ],
            {
              success: "Sehr gut – dein Text hat jetzt eine erkennbare Gliederung.",
              failure: "Die Hierarchie steht noch nicht vollständig.",
              hint: "Prüfe, ob wirklich der ganze Absatz dieselbe Größe hat. Ein einzelnes Wort in anderer Größe reicht schon, damit die Prüfung fehlschlägt.",
            },
          );
        },
      },
    },
  ],
};
